import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { language, t } = useLanguage();

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        // Seed posts first
        await axios.post(`${API}/seed-blog`).catch(() => {});
        
        const response = await axios.get(`${API}/blog`);
        setPosts(response.data);
      } catch (e) {
        console.error("Error fetching blog posts:", e);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPosts();
  }, []);

  const getTitle = (post) => {
    return language === 'en' && post.title_en ? post.title_en : post.title;
  };

  const getExcerpt = (post) => {
    return language === 'en' && post.excerpt_en ? post.excerpt_en : post.excerpt;
  };

  return (
    <div data-testid="blog-page">
      {/* Page Header */}
      <section className="page-header py-20 md:py-28 text-white">
        <div className="container mx-auto px-4 relative z-10">
          <span className="inline-block bg-orange-600 text-white text-sm uppercase tracking-widest font-bold px-4 py-2 mb-6">
            {language === 'de' ? 'Aktuelles' : 'News'}
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            {language === 'de' ? 'Blog & News' : 'Blog & News'}
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl">
            {language === 'de' 
              ? 'Neuigkeiten, Einblicke und Wissenswertes aus der Welt der Logistik.'
              : 'News, insights and interesting facts from the world of logistics.'}
          </p>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-slate-100 animate-pulse">
                  <div className="aspect-video bg-slate-200"></div>
                  <div className="p-6">
                    <div className="h-4 bg-slate-200 w-24 mb-4"></div>
                    <div className="h-6 bg-slate-200 w-full mb-2"></div>
                    <div className="h-4 bg-slate-200 w-3/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                {language === 'de' ? 'Noch keine Beiträge' : 'No posts yet'}
              </h3>
              <p className="text-slate-600">
                {language === 'de' 
                  ? 'Bald finden Sie hier aktuelle News und interessante Artikel.'
                  : 'Soon you will find current news and interesting articles here.'}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="blog-card bg-white border border-slate-200 overflow-hidden group"
                  data-testid={`blog-post-${index}`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={post.image_url || "https://images.unsplash.com/photo-1604464543433-149fe779b779?w=600"}
                      alt={getTitle(post)}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                      <Calendar size={14} aria-hidden="true" />
                      <span>
                        {new Date(post.created_at).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                    <h2 className="font-bold text-xl text-slate-900 mb-3 group-hover:text-orange-600 transition-colors">
                      {getTitle(post)}
                    </h2>
                    <p className="text-slate-600 mb-4 line-clamp-3">
                      {getExcerpt(post)}
                    </p>
                    <span className="inline-flex items-center text-orange-600 font-semibold gap-2 group-hover:gap-3 transition-all">
                      {t('blog.readMore')}
                      <ArrowRight size={16} aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
